import React, { useEffect, useState } from "react";
import { Download, Share, Plus, X } from "lucide-react";

const DISMISS_KEY = "thed_install_dismissed_at";
const DISMISS_DAYS = 7;

function isStandalone() {
  return window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
}
function isIOS() {
  const ua = window.navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}
function recentlyDismissed() {
  const t = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
  return t && (Date.now() - t) < DISMISS_DAYS * 864e5;
}

// Tasteful "Install The D App" affordance + iOS Add-to-Home-Screen guidance.
// variant: "button" (inline) | "link" (text)
export default function InstallApp({ variant = "button", className = "" }) {
  const [deferred, setDeferred] = useState(() => window.__thedDeferredPrompt || null);
  const [installed, setInstalled] = useState(isStandalone());
  const [showIOS, setShowIOS] = useState(false);
  const ios = isIOS();

  useEffect(() => {
    const onBip = (e) => { e.preventDefault(); window.__thedDeferredPrompt = e; setDeferred(e); };
    const onInstalled = () => { setInstalled(true); window.__thedDeferredPrompt = null; };
    window.addEventListener("beforeinstallprompt", onBip);
    window.addEventListener("appinstalled", onInstalled);
    return () => { window.removeEventListener("beforeinstallprompt", onBip); window.removeEventListener("appinstalled", onInstalled); };
  }, []);

  if (installed) return null;
  // Show on iOS Safari always (no native prompt); elsewhere only when installable.
  if (!ios && !deferred) return null;
  if (recentlyDismissed() && !deferred) return null;

  const click = async () => {
    if (deferred) { deferred.prompt(); await deferred.userChoice; window.__thedDeferredPrompt = null; setDeferred(null); return; }
    if (ios) setShowIOS(true);
  };

  const label = ios ? "Add The D to Your Home Screen" : "Install The D App";

  return (
    <>
      {variant === "link" ? (
        <button onClick={click} data-testid="install-app-link" className={`text-xs tracking-label uppercase text-gold hover:underline inline-flex items-center gap-1.5 ${className}`}>
          <Download size={13} /> {label}
        </button>
      ) : (
        <button onClick={click} data-testid="install-app-btn" className={`btn-ghost-gold px-4 py-2 text-xs inline-flex items-center gap-2 ${className}`}>
          <Download size={14} /> {label}
        </button>
      )}

      {showIOS && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-6 bg-ink/80 backdrop-blur-sm" data-testid="ios-install-guide" onClick={() => setShowIOS(false)}>
          <div className="glass-dark border rounded-2xl max-w-sm w-full p-6 text-center" style={{ borderColor: "rgba(212,175,55,0.35)" }} onClick={(e) => e.stopPropagation()}>
            <button onClick={() => { setShowIOS(false); localStorage.setItem(DISMISS_KEY, String(Date.now())); }} aria-label="Close" className="float-right text-muted-foreground hover:text-gold"><X size={16} /></button>
            <img src="/icons/icon-180.png" alt="The D" className="h-16 w-16 rounded-2xl mx-auto mb-4" />
            <h3 className="font-serif text-2xl text-gold">Install The D</h3>
            <p className="text-sm text-muted-foreground mt-1 mb-5">Add The D to your iPhone or iPad home screen.</p>
            <ol className="text-sm text-left space-y-3">
              <li className="flex items-center gap-3"><span className="text-gold">1.</span> Tap the <Share size={15} className="text-pool inline" /> <b>Share</b> button.</li>
              <li className="flex items-center gap-3"><span className="text-gold">2.</span> Choose <Plus size={15} className="text-pool inline" /> <b>Add to Home Screen</b>.</li>
              <li className="flex items-center gap-3"><span className="text-gold">3.</span> Tap <b>Add</b>.</li>
            </ol>
            <button onClick={() => { setShowIOS(false); localStorage.setItem(DISMISS_KEY, String(Date.now())); }} className="btn-gold w-full py-2.5 text-sm mt-6" data-testid="ios-guide-done">Got it</button>
          </div>
        </div>
      )}
    </>
  );
}
